import * as React from 'react';
import type { HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { FontWeight, FontFamily } from './Typography.types';
import { useTypography } from './TypographyContext';
import { getFontClass } from './typography.utils';

// Enum for heading variants
export enum HeadingVariant {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
  H6 = 'h6',
  H7 = 'h7',
  H8 = 'h8',
  H9 = 'h9',
  H10 = 'h10',
  H11 = 'h11',
  H12 = 'h12',
}

// Map variants to specific heading tags
const mapVariantToTag = {
  [HeadingVariant.H1]: 'h1',
  [HeadingVariant.H2]: 'h1',
  [HeadingVariant.H3]: 'h2',
  [HeadingVariant.H4]: 'h2',
  [HeadingVariant.H5]: 'h3',
  [HeadingVariant.H6]: 'h3',
  [HeadingVariant.H7]: 'h4',
  [HeadingVariant.H8]: 'h4',
  [HeadingVariant.H9]: 'h5',
  [HeadingVariant.H10]: 'h5',
  [HeadingVariant.H11]: 'h6',
  [HeadingVariant.H12]: 'h6',
};

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  fontWeight?: FontWeight;
  fontFamily?: FontFamily;
  children: React.ReactNode | string;
  variant?: HeadingVariant;
  id?: string;
  /** never add override to the text size, line height, and weight */
  className?: string;
  testId?: string;
}

// Map variants to specific heading classes
const mapVariantToClass = {
  [HeadingVariant.H1]: 'text-[48px] leading-[56px] text-text-main-primary',
  [HeadingVariant.H2]: 'text-[40px] leading-[48px] text-text-main-primary',
  [HeadingVariant.H3]: 'text-[32px] leading-[38px] text-text-main-primary',
  [HeadingVariant.H4]: 'text-[28px] leading-[34px] text-text-main-primary',
  [HeadingVariant.H5]: 'text-[24px] leading-[32px] text-text-main-primary',
  [HeadingVariant.H6]: 'text-[22px] leading-[28px] text-text-main-primary',
  [HeadingVariant.H7]: 'text-[20px] leading-[26px] text-text-main-primary',
  [HeadingVariant.H8]: 'text-[18px] leading-[24px] text-text-main-primary',
  [HeadingVariant.H9]: 'text-[16px] leading-[24px] text-text-main-primary',
  [HeadingVariant.H10]: 'text-[14px] leading-[16px] text-text-main-primary',
  [HeadingVariant.H11]: 'text-[13px] leading-[16px] text-text-main-primary',
  [HeadingVariant.H12]: 'text-[12px] leading-[14px] text-text-main-primary',
};

// Base Heading component
export const Heading = (props: HeadingProps) => {
  const {
    fontWeight = FontWeight.REGULAR,
    fontFamily,
    children,
    className,
    variant = HeadingVariant.H1,
    id,
    testId,
    ...rest
  } = props;

  const { defaultFontFamily } = useTypography();

  // Determine which font family to use: prop > context > none (for backward compatibility)
  const effectiveFontFamily = fontFamily || defaultFontFamily;

  const headingClass = mapVariantToClass[variant];
  const fontClass = getFontClass(fontWeight, effectiveFontFamily);
  const style = twMerge(headingClass, fontClass, className);

  const Element = mapVariantToTag[variant] as keyof React.JSX.IntrinsicElements; // Ensure Element is one of the intrinsic HTML elements

  return React.createElement(
    Element,
    {
      className: style,
      id,
      'data-testid': testId,
      ...rest,
    },
    children
  );
};

Heading.H1 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H1} />
);
Heading.H2 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H2} />
);
Heading.H3 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H3} />
);
Heading.H4 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H4} />
);
Heading.H5 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H5} />
);
Heading.H6 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H6} />
);
Heading.H7 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H7} />
);
Heading.H8 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H8} />
);
Heading.H9 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H9} />
);
Heading.H10 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H10} />
);
Heading.H11 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H11} />
);
Heading.H12 = (props: Omit<HeadingProps, 'variant'>) => (
  <Heading {...props} variant={HeadingVariant.H12} />
);
